import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown } from 'react-icons/fi';
import './Dropdown.css';

const Dropdown = ({ label, value, onChange, options, placeholder = 'აირჩიეთ', compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    // Close if trigger scrolled out of view
    if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
      setIsOpen(false);
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const maxMenu = compact ? 200 : 300;
    const menuHeight = Math.min(options.length * 40 + 2, maxMenu);

    // Open up if more space above than below
    const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    const availableSpace = openUp ? spaceAbove - 8 : spaceBelow - 8;
    const finalMaxHeight = Math.max(Math.min(menuHeight, availableSpace, maxMenu), 80);

    // Clamp horizontal position to viewport
    const menuWidth = Math.max(rect.width, compact ? 180 : 0);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));

    setMenuStyle({
      position: 'fixed',
      left,
      width: menuWidth,
      zIndex: 99999,
      maxHeight: finalMaxHeight,
      overflowY: 'auto',
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [compact, options.length]);

  useEffect(() => {
    if (isOpen) {
      calcPosition();
      const onScrollOrResize = () => calcPosition();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    }
  }, [isOpen, calcPosition]);

  return (
    <div className={`dropdown-wrapper ${compact ? 'dropdown-compact' : ''}`} ref={ref}>
      {label && <label className="dropdown-label">{label}</label>}
      <button
        type="button"
        ref={triggerRef}
        className={`dropdown-trigger ${isOpen ? 'open' : ''} ${value !== '' && value != null ? 'has-value' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value !== '' && value != null && selected ? '' : 'placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <FiChevronDown className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`} />
      </button>
      {isOpen && createPortal(
        <div className="dropdown-menu" style={menuStyle} ref={menuRef}>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`dropdown-item ${String(value) === String(opt.value) ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Dropdown;
